import { zxcvbn, zxcvbnOptions, type ZxcvbnResult } from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';
import type { RefinementCtx } from 'zod';

let isInitialized = false;

function ensureInitialized() {
  if (isInitialized) {
    return;
  }

  zxcvbnOptions.setOptions({
    translations: zxcvbnEnPackage.translations,
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
  });

  isInitialized = true;
}

export function evaluatePassword(password: string, userInputs: string[] = []): ZxcvbnResult {
  ensureInitialized();

  return zxcvbn(password, userInputs);
}

export type PasswordEvaluation = ZxcvbnResult;

export const MIN_PASSWORD_LENGTH = 8;
export const MIN_PASSWORD_SCORE = 3;
export const PASSWORD_STRENGTH_LABELS = ['Weak', 'Weak', 'Weak', 'OK but not great', 'Great'] as const;
export const PASSWORD_STRENGTH_COLORS = ['bg-red-500', 'bg-red-500', 'bg-red-500', 'bg-yellow-500', 'bg-green-500'] as const;
const DEFAULT_WEAK_PASSWORD_MESSAGE = 'Password is too weak. Please choose a stronger password.';

type PasswordStrengthRefinementOptions<T> = {
  userInputsResolver?: (data: T) => Array<string | undefined | null>;
  path?: (string | number)[];
  minScore?: number;
  message?: string;
};

export function createPasswordStrengthRefinement<T extends { password?: string | null }>(
  options: PasswordStrengthRefinementOptions<T> = {},
) {
  const {
    userInputsResolver = () => [],
    path = ['password'],
    minScore = MIN_PASSWORD_SCORE,
    message = DEFAULT_WEAK_PASSWORD_MESSAGE,
  } = options;

  return (data: T, ctx: RefinementCtx) => {
    if (!data.password) {
      return;
    }

    const normalizedInputs = Array.from(
      new Set(
        userInputsResolver(data)
          .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
          .filter((value): value is string => Boolean(value)),
      ),
    );

    const result = evaluatePassword(data.password, normalizedInputs);

    if (result.score < minScore) {
      ctx.addIssue({
        code: 'custom',
        message,
        path,
      });
    }
  };
}
